import { useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { authService, LoginCredentials } from '../services/authService';

export const useAuth = () => {
    const {
        user,
        isAuthenticated,
        accessToken,
        refreshToken,
        tokenExpiry,
        login,
        logout,
        updateTokens,
        isTokenExpired
    } = useAuthStore();

    // 自动刷新Token
    const refreshTokenIfNeeded = useCallback(async () => {
        if (!refreshToken || !isTokenExpired()) {
            return true;
        }

        try {
            const response = await authService.refreshToken(refreshToken);
            updateTokens({
                access_token: response.access_token,
                refresh_token: response.refresh_token,
                expires_in: response.expires_in
            });
            return true;
        } catch (error) {
            console.error('Token refresh failed:', error);
            logout();
            return false;
        }
    }, [refreshToken, isTokenExpired, updateTokens, logout]);

    // 检查本地存储的认证信息
    useEffect(() => {
        const checkAuth = async () => {
            console.log('🔍 检查认证状态:', { user, accessToken, isAuthenticated });
            
            if (!user || !accessToken) {
                console.log('❌ 用户或Token为空，跳过验证');
                return;
            }

            // 检查Token是否过期
            if (isTokenExpired()) {
                console.log('⏰ Token已过期，尝试刷新...');
                const refreshed = await refreshTokenIfNeeded();
                if (!refreshed) {
                    console.log('❌ Token刷新失败，执行登出');
                    logout();
                    return;
                }
                console.log('✅ Token刷新成功');
            }

            // 验证Token是否仍然有效（注意使用刷新后的最新 token，而非闭包旧值）
            try {
                console.log('🔍 验证Token有效性...');
                const currentToken = useAuthStore.getState().accessToken;
                if (!currentToken) return;
                const validation = await authService.validateToken(currentToken);
                if (!validation.valid) {
                    console.log('❌ Token验证失败:', validation.error);
                    logout();
                } else {
                    console.log('✅ Token验证成功');
                }
            } catch (error) {
                // 2026-09-18 用户令"登录过就别再录"：网络级失败（后端重启/抖动）不得踢人，
                // 只有服务端明确判无效才登出——否则栈重启瞬间在线用户全掉线
                console.error('⚠️ Token 验证请求失败（网络/服务抖动），保持登录态待下次验证:', error);
            }
        };

        checkAuth();
    }, [user, accessToken, isTokenExpired, refreshTokenIfNeeded, logout]);

    const handleLogin = async (credentials: LoginCredentials) => {
        try {
            const response = await authService.login(credentials);

            // 更新用户信息和Token
            login(
                {
                    id: response.user.id,
                    username: response.user.username,
                    email: response.user.email,
                    first_name: response.user.first_name,
                    last_name: response.user.last_name,
                    authString: `Bearer ${response.access_token}`, // 兼容旧版本
                    timestamp: new Date().toISOString(),
                },
                {
                    access_token: response.access_token,
                    refresh_token: response.refresh_token,
                    expires_in: response.expires_in
                }
            );

            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : '登录失败',
            };
        }
    };

    const handleLogout = async () => {
        try {
            await authService.logout(refreshToken || undefined);
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // 即使登出失败，也要清除本地状态
            logout();
        }
    };

    const handleDemoLogin = () => {
        const demoUser = {
            id: 0,
            username: 'demo',
            email: 'demo@example.com',
            first_name: 'Demo',
            last_name: 'User',
            authString: btoa('demo:demo123'), // Basic Auth for demo
            timestamp: new Date().toISOString(),
        };

        // Demo用户使用Basic Auth，不需要JWT Token
        login(demoUser, {
            access_token: '',
            refresh_token: '',
            expires_in: 24 * 60 * 60 // 24小时
        });
    };

    // 获取当前有效的认证字符串
    const getAuthString = useCallback(() => {
        if (accessToken) {
            return `Bearer ${accessToken}`;
        }
        if (user?.authString) {
            return user.authString.startsWith('Basic ') ? user.authString : `Basic ${user.authString}`;
        }
        return null;
    }, [accessToken, user?.authString]);

    return {
        user,
        isAuthenticated,
        accessToken,
        refreshToken,
        tokenExpiry,
        login: handleLogin,
        logout: handleLogout,
        demoLogin: handleDemoLogin,
        refreshTokenIfNeeded,
        getAuthString,
        isTokenExpired,
    };
};